import type { ApiError } from '@honeycomb/shared';
import { API_BASE_URL } from '@/env';
import { getToken } from './token';

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: ApiError['error']['code'] | 'NETWORK';
  readonly details?: unknown;
  constructor(
    status: number,
    code: ApiClientError['code'],
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export type RequestOptions = Omit<RequestInit, 'body'> & {
  json?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
};

export async function apiFetch<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const url = new URL(path, API_BASE_URL);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  const headers = new Headers(opts.headers);
  if (opts.json !== undefined) headers.set('content-type', 'application/json');

  headers.set('X-Honeycomb-Features', 'tags:ingredient,tags:technique');

  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      ...opts,
      headers,
      body:
        opts.json !== undefined
          ? JSON.stringify(opts.json)
          : (opts as RequestInit).body ?? undefined,
    });
  } catch (e) {
    throw new ApiClientError(
      0,
      'NETWORK',
      e instanceof Error ? e.message : 'Network request failed',
    );
  }

  if (res.status === 204) return undefined as T;

  let body: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      /* non-JSON */
    }
  }

  if (!res.ok) {
    const err = (body as { error?: ApiError['error'] })?.error;
    throw new ApiClientError(
      res.status,
      err?.code ?? 'INTERNAL',
      err?.message ?? res.statusText,
      err?.details,
    );
  }
  return body as T;
}

export async function apiFetchBlob(path: string, opts: RequestOptions = {}): Promise<Blob> {
  const url = new URL(path, API_BASE_URL);
  if (opts.query) {
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined) url.searchParams.set(k, String(v));
    }
  }
  const headers = new Headers(opts.headers);
  headers.set('X-Honeycomb-Features', 'tags:ingredient,tags:technique');
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let res: Response;
  try {
    res = await fetch(url.toString(), { ...opts, headers });
  } catch (e) {
    throw new ApiClientError(0, 'NETWORK', e instanceof Error ? e.message : 'Network request failed');
  }
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const body = (await res.json()) as { error?: { message?: string; code?: string } };
      if (body?.error?.message) msg = body.error.message;
      throw new ApiClientError(res.status, (body?.error?.code ?? 'INTERNAL') as ApiClientError['code'], msg);
    } catch (inner) {
      if (inner instanceof ApiClientError) throw inner;
      throw new ApiClientError(res.status, 'INTERNAL', msg);
    }
  }
  return await res.blob();
}
